import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BID_STATUS_LABEL } from '@/shared/constants/bids';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { fetchUserBids } from './api';
import { PagedBox } from './PagedBox';

interface BidRow extends Record<string, unknown> {
  id: number;
  create_time?: number;
  description?: string;
  status_id?: number;
  answers_count?: number;
  auto?: { mark?: { name?: string } | null; model?: { name?: string } | null } | null;
  customer?: { name?: string } | null;
  region?: { name?: string } | null;
  city?: { name?: string } | null;
  utm_source?: string;
  partner_name?: string;
  attached_promo_code?: { code?: string } | null;
}

const DESCRIPTION_LIMIT = 40;

const truncate = (text = '') => (text.length > DESCRIPTION_LIMIT ? `${text.slice(0, DESCRIPTION_LIMIT - 3)}...` : text);

type Props = {
  userId: string | number;
  isService: boolean;
};

export const UserBidsBox = ({ userId, isService }: Props) => {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useQuery({
    queryKey: ['user', String(userId), 'bids', isService, page],
    queryFn: () => fetchUserBids(userId, isService, page),
  });
  const rows = getListRows<BidRow>(data, ['bids']);

  return (
    <PagedBox
      title={`Список заявок (${rows.length})`}
      isLoading={isLoading}
      error={error}
      page={page}
      totalPages={getTotalPages(data)}
      onPageChange={setPage}
    >
      <table className="table table-bordered table-hover">
        <thead>
          <tr>
            <th>id</th>
            <th>Марка</th>
            <th>Модель</th>
            <th>Дата создания</th>
            <th>Описание</th>
            {isService && <th>Клиент</th>}
            <th>Статус</th>
            <th>Регион</th>
            <th>Город</th>
            <th>Источник</th>
            <th>Лояльность</th>
            <th>Партнер</th>
            <th>Кол-во сервисов</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((bid) => (
            <tr key={bid.id}>
              <td>
                <Link to={`/bids/bid/${bid.id}`}>{bid.id}</Link>
              </td>
              <td>{bid.auto?.mark?.name}</td>
              <td>{bid.auto?.model?.name}</td>
              <td>{formatUnixDateTime(bid.create_time)}</td>
              <td>{truncate(bid.description)}</td>
              {isService && <td>{bid.customer?.name}</td>}
              <td>{BID_STATUS_LABEL[Number(bid.status_id)] ?? `Статус неопределен: ${bid.status_id}`}</td>
              <td>{bid.region?.name}</td>
              <td>{bid.city?.name}</td>
              <td>{bid.utm_source}</td>
              <td>{bid.attached_promo_code?.code}</td>
              <td>{bid.partner_name}</td>
              <td>{bid.answers_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PagedBox>
  );
};
